import { ChatMessage as SquareKitChatMessage } from '@parentsquare/squarekit/molecules/chat/ChatMessage';
import React from 'react';
import { getAvatarInitials } from '@/Public/ChatBot/Constants';
import { useChatBotTheme } from '@/Public/ChatBot/Hooks/useChatBotTheme';
import type { ChatMessage as ChatMessageType } from '@/Public/ChatBot/Types';
import { renderAvatar } from './BotAvatar';
import { CitationsList } from './CitationsList';
import { MessageActions } from './MessageActions';

interface ChatMessageProps {
    readonly message: ChatMessageType;
    readonly onRequestFeedback?: (messageId: string, direction: 'up' | 'down') => void;
    readonly hideActions?: boolean;
    readonly isFeedbackOpen?: boolean;
}

function formatTime(date: Date): string {
    return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

export const ChatMessage = React.forwardRef<HTMLDivElement, ChatMessageProps>(
    ({ message, onRequestFeedback, hideActions, isFeedbackOpen }, ref): React.ReactNode => {
        const { assistantName, avatarType, avatarImageUrl } = useChatBotTheme();
        const isUser = message.role === 'user';
        const citations = !isUser && message.citations?.length ? message.citations : [];
        const initials = getAvatarInitials(assistantName);

        const handleRequestFeedback = (direction: 'up' | 'down'): void => {
            onRequestFeedback?.(message.id, direction);
        };

        return (
            <div ref={ref}>
                <SquareKitChatMessage
                    authorName={isUser ? '' : assistantName}
                    timestamp={formatTime(message.timestamp)}
                    cardClassName={isUser ? 'tw:[&>div:first-child]:hidden' : undefined}
                    body={
                        <>
                            <span className="tw:[white-space:break-spaces]">{message.content}</span>
                            {citations.length > 0 && <CitationsList citations={citations} />}
                        </>
                    }
                    isCurrentUser={isUser}
                    avatar={
                        !isUser ? renderAvatar(avatarType, initials, avatarImageUrl, assistantName, 'sm') : undefined
                    }
                    reactions={
                        !isUser ? (
                            <div className={`tw:mt-2${hideActions ? ' tw:invisible' : ''}`}>
                                <MessageActions
                                    content={message.content}
                                    citations={citations}
                                    feedback={message.feedback}
                                    onRequestFeedback={handleRequestFeedback}
                                    isFeedbackOpen={isFeedbackOpen}
                                />
                            </div>
                        ) : undefined
                    }
                />
            </div>
        );
    },
);
ChatMessage.displayName = 'ChatMessage';
